import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import whatsappIcon from "../assets/what-icon.png";
import { useAuth } from "../AuthProvider";
import { useContact } from "../hooks/useContact";
import { useCustomColors } from "../hooks/useCustomColors";
import { MOBILE_NAV_HEIGHT } from "./lotto/MobileBottomNav";

// Each glow stays inside its size's gap to the screen edge (24 / 20 / 16px), so it is never cut.
const HALO = "0 0 0 1px rgba(70, 4, 4, 0.9), 0 0 10px rgba(255, 45, 35, 0.75), 0 0 20px rgba(120, 0, 0, 0.65)";
const HALO_HOVER = "0 0 0 1px rgba(90, 6, 6, 0.95), 0 0 13px rgba(255, 60, 45, 0.95), 0 0 19px rgba(212, 175, 55, 0.4)";
const HALO_MOBILE = "0 0 0 1px rgba(70, 4, 4, 0.9), 0 0 6px rgba(255, 45, 35, 0.65), 0 0 12px rgba(120, 0, 0, 0.5)";
const HALO_MOBILE_HOVER = "0 0 0 1px rgba(90, 6, 6, 0.95), 0 0 8px rgba(255, 60, 45, 0.9), 0 0 14px rgba(212, 175, 55, 0.3)";

/** A circle behind the image; the image covers all of it but the ring band. */
const RING_LAYER = {
  content: '""',
  position: "absolute",
  inset: 0,
  zIndex: -1,
  borderRadius: "50%",
  pointerEvents: "none",
} as const;

const SupportFloatingButton = () => {
  const { t } = useTranslation();
  const colors = useCustomColors();
  const { user } = useAuth();
  const { reviewUrl } = useContact();

  if (!reviewUrl) return null;

  const gold = colors.accent.gold;
  // Signed-in pages below md carry the bottom nav; sit above it.
  const navOffset = user ? `${MOBILE_NAV_HEIGHT}px + ` : "";

  return (
    <Box
      component="a"
      href={reviewUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("support_fab_label")}
      sx={{
        position: "fixed",
        zIndex: (theme) => theme.zIndex.speedDial,
        right: { xs: 16, sm: 20, md: 24 },
        bottom: {
          xs: `calc(${navOffset}16px + env(safe-area-inset-bottom))`,
          sm: `calc(${navOffset}20px + env(safe-area-inset-bottom))`,
          md: "24px",
        },
        width: { xs: 44, sm: 50, md: 58 },
        height: { xs: 44, sm: 50, md: 58 },
        // The LED ring's thickness; the image sits flush inside it.
        p: { xs: "2px", md: "3px" },
        boxSizing: "border-box",
        display: "block",
        overflow: "visible",
        isolation: "isolate",
        borderRadius: "50%",
        boxShadow: { xs: HALO_MOBILE, sm: HALO },
        animation: "supportFloat 3.6s ease-in-out infinite",
        transition: "box-shadow 0.35s ease",
        // LED ring: red and gold chasing each other round the circle.
        "&::before": {
          ...RING_LAYER,
          background: `conic-gradient(#ff3b30, ${gold}, #7a0a0a, #ff3b30, ${gold}, #7a0a0a, #ff3b30)`,
          animation: "supportSpin 4s linear infinite",
        },
        // A single gold highlight travelling round the ring the other way.
        "&::after": {
          ...RING_LAYER,
          background: "conic-gradient(transparent 0 78%, rgba(255, 232, 160, 0.95) 88%, transparent 96%)",
          animation: "supportSpin 3.2s linear infinite reverse",
        },
        "&:hover": { boxShadow: { xs: HALO_MOBILE_HOVER, sm: HALO_HOVER } },
        "&:focus-visible": {
          outline: `2px solid ${gold}`,
          outlineOffset: "4px",
          boxShadow: { xs: HALO_MOBILE_HOVER, sm: HALO_HOVER },
        },
        "@keyframes supportFloat": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-5px)" },
        },
        "@keyframes supportSpin": {
          to: { rotate: "1turn" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transition: "none",
          "&::before": { animation: "none" },
          "&::after": { animation: "none", opacity: 0 },
        },
      }}
    >
      <Box
        component="img"
        src={whatsappIcon}
        alt={t("support_fab_alt")}
        draggable={false}
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
          m: 0,
          p: "4px",
          boxSizing: "border-box",
          borderRadius: "12px",
          objectFit: "contain",
          // Thin gold inner ring, and a dark base for the PNG's transparent edges.
        }}
      />
    </Box>
  );
};

export default SupportFloatingButton;
